import { useCallback, useEffect, useRef } from 'react';

import type { PusherFallbackOptions, PusherServiceInterface } from './use-label-batch-progress';
import type { LtlQuote, LtlQuoteCharge } from '../../types/ltl';
import type { MutableRefObject } from 'react';

type RawBusinessHours = {
  DayOfWeek: string | null;
  BeginTime: string | null;
  EndTime: string | null;
  IsClosed: boolean;
  IsOpen24Hours: boolean;
};

export type RawFreightQuoteCharge = {
  Category: string;
  Code: string;
  Description: string;
  CarrierChargeCode: string | null;
  Price: number;
};

export type RawFreightQuote = {
  OfferId: string;
  ProductTransactionId: string;
  CarrierName: string;
  CarrierScac: string;
  ServiceName: string;
  TotalCharges: number;
  TotalHandlingCharge: number | null;
  TransitDays: number;
  EstimatedDeliveryDate: string;
  IsGuaranteed: boolean;
  ServiceType: string | null;
  OriginTerminalCode: string | null;
  DestinationTerminalCode: string | null;
  TransitDistance: number | null;
  TransitDistanceUnit: string | null;
  ScacList: string[];
  Charges: RawFreightQuoteCharge[];
  InsuredAmount: number | null;
  InsurancePremium: number | null;
  InsuranceCertificateNumber: string | null;
  InsuranceDeductible: number | null;
  InsuranceTermsAndConditions: string | null;
  InsuranceQuoteDate: string | null;
  InsuranceQuoteExpirationDate: string | null;
  QuoteExpirationDate: string | null;
  MaxLiabilityNew: number | null;
  MaxLiabilityUsed: number | null;
  PickupDays: string[];
  LatestPickupTime: string | null;
  PickupWindowMinutes: number | null;
  NotificationWindowMinutes: number | null;
  BusinessHours: RawBusinessHours[];
};

type FreightQuoteMessageData = {
  IsComplete: boolean;
  IsSuccess: boolean;
  Errors?: { Code: string; Message: string }[] | null;
  ProductTransactionId: string | null;
  Quote: RawFreightQuote | null;
  QuoteIndex: number;
  TotalQuotes: number;
};

export type FreightQuotePusherMessage = {
  messageGuid: string;
  data: FreightQuoteMessageData;
};

export type UseFreightQuoteProgressOptions = {
  pusherService: PusherServiceInterface;
  pendingGuidsRef: MutableRefObject<Set<string>>;
  onQuotes: (quotes: LtlQuote[]) => void;
  onError: (message: string, code?: string | null) => void;
  onComplete: () => void;
  timeoutMs?: number;
};

export type UseFreightQuoteProgressReturn = {
  startListening: (jobGuids: string[]) => void;
  cancelListening: () => void;
};

export const mapPascalCaseQuoteOptions = (raw: RawFreightQuote): LtlQuote => ({
  offerId: raw.OfferId ?? '',
  productTransactionId: raw.ProductTransactionId ?? '',
  carrierName: raw.CarrierName ?? '',
  carrierScac: raw.CarrierScac ?? '',
  serviceName: raw.ServiceName ?? '',
  totalCharges: raw.TotalCharges ?? 0,
  transitDays: raw.TransitDays ?? 0,
  estimatedDeliveryDate: raw.EstimatedDeliveryDate ?? '',
  isGuaranteed: raw.IsGuaranteed ?? false,
  quoteExpirationDate: raw.QuoteExpirationDate ?? null,
  serviceType: raw.ServiceType ?? null,
  originTerminalCode: raw.OriginTerminalCode ?? null,
  destinationTerminalCode: raw.DestinationTerminalCode ?? null,
  insuredAmount: raw.InsuredAmount ?? null,
  insurancePremium: raw.InsurancePremium ?? null,
  insuranceCertificateNumber: raw.InsuranceCertificateNumber ?? null,
  totalHandlingCharge: raw.TotalHandlingCharge ?? null,
  transitDistance: raw.TransitDistance ?? null,
  transitDistanceUnit: raw.TransitDistanceUnit ?? null,
  scacList: raw.ScacList ?? [],
  insuranceDeductible: raw.InsuranceDeductible ?? null,
  insuranceTermsAndConditions: raw.InsuranceTermsAndConditions ?? null,
  insuranceQuoteDate: raw.InsuranceQuoteDate ?? null,
  insuranceQuoteExpirationDate: raw.InsuranceQuoteExpirationDate ?? null,
  maxLiabilityNew: raw.MaxLiabilityNew ?? null,
  maxLiabilityUsed: raw.MaxLiabilityUsed ?? null,
  pickupDays: raw.PickupDays ?? [],
  latestPickupTime: raw.LatestPickupTime ?? null,
  pickupWindowMinutes: raw.PickupWindowMinutes ?? null,
  notificationWindowMinutes: raw.NotificationWindowMinutes ?? null,
  businessHours: (raw.BusinessHours ?? []).map((h) => ({
    dayOfWeek: h.DayOfWeek ?? null,
    beginTime: h.BeginTime ?? null,
    endTime: h.EndTime ?? null,
    isClosed: h.IsClosed ?? false,
    isOpen24Hours: h.IsOpen24Hours ?? false,
  })),
  charges: (raw.Charges ?? []).map(
    (c): LtlQuoteCharge => ({
      category: c.Category ?? '',
      code: c.Code ?? '',
      description: c.Description ?? '',
      carrierChargeCode: c.CarrierChargeCode ?? null,
      price: c.Price ?? 0,
    }),
  ),
});

const TIMEOUT_MESSAGE = 'Quote request timed out. Please try again.';
const DEFAULT_TIMEOUT_MS = 30000;

export const useFreightQuoteProgress = ({
  pusherService,
  pendingGuidsRef,
  onQuotes,
  onError,
  onComplete,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UseFreightQuoteProgressOptions): UseFreightQuoteProgressReturn => {
  const onQuotesRef = useRef(onQuotes);
  onQuotesRef.current = onQuotes;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const handleMessage = useCallback((message: FreightQuotePusherMessage) => {
    const { messageGuid, data } = message;

    if (!pendingGuidsRef.current.has(messageGuid)) return;

    if (data.Quote !== null) {
      onQuotesRef.current([mapPascalCaseQuoteOptions(data.Quote)]);
    }

    if (data.IsComplete) {
      pendingGuidsRef.current.delete(messageGuid);

      if (data.Errors?.length) {
        onErrorRef.current(data.Errors[0].Message, data.Errors[0].Code);
      }

      if (pendingGuidsRef.current.size === 0) {
        onCompleteRef.current();
      }
    }
    // pendingGuidsRef is a stable ref — .current is always fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fallbackOptions: PusherFallbackOptions = {
      timeoutMs,
      onTimeout: () => {
        if (pendingGuidsRef.current.size === 0) return;
        pendingGuidsRef.current.clear();
        onErrorRef.current(TIMEOUT_MESSAGE);
        onCompleteRef.current();
      },
    };

    const unsubscribe = pusherService.subscribe<FreightQuotePusherMessage>('freight-quote', handleMessage, fallbackOptions);

    return unsubscribe;
    // pendingGuidsRef is a stable ref — .current is always fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pusherService, timeoutMs, handleMessage]);

  const startListening = useCallback((jobGuids: string[]) => {
    pendingGuidsRef.current.clear();
    jobGuids.forEach((guid) => pendingGuidsRef.current.add(guid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelListening = useCallback(() => {
    pendingGuidsRef.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { startListening, cancelListening };
};
